import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, CalendarDays, CalendarPlus, Tag, Ticket } from "lucide-react";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { SITE_URL, SITE_NAME } from "@/constants/site";
import { api, API } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";

export default function EventDetail() {
  const { id, city } = useParams();
  const navigate = useNavigate();
  const { sportLabel, teamLabel } = useCategories();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/events/${id}`)
      .then(({ data }) => setEvent(data))
      .catch(() => setError(true));
  }, [id]);

  if (error)
    return (
      <div className="App">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-24 text-center">
          <p className="font-heading text-4xl text-zinc-400">Event not found</p>
          <button onClick={() => navigate(`/${city}`)} className="overline text-ec-volt mt-4">← Back to calendar</button>
        </div>
      </div>
    );

  if (!event)
    return (
      <div className="App">
        <Navbar />
        <p className="text-center text-zinc-500 overline py-24">Loading…</p>
      </div>
    );

  const d = new Date(event.start_at);
  const single = `${API}/ics?city=${event.city || "worcester"}&teams=${event.team || ""}`.replace(/^https?:\/\//, "webcal://");

  const dateStr = d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const team = event.team ? teamLabel(event.team) : "";
  const seoTitle = `${event.title} — ${event.venue}, ${dateStr} | ${SITE_NAME}`;
  const seoDesc = (event.description && event.description.length > 20)
    ? event.description
    : `${event.title} at ${event.venue} on ${dateStr}. Find fixture details, tickets and add it to your calendar with ${SITE_NAME}.`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": event.sport === "arts" ? "TheaterEvent" : "SportsEvent",
    name: event.title,
    startDate: event.start_at,
    ...(event.end_at ? { endDate: event.end_at } : {}),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: { "@type": "Place", name: event.venue, address: { "@type": "PostalAddress", addressLocality: event.city, addressCountry: "GB" } },
    ...(event.image ? { image: [event.image] } : {}),
    description: seoDesc,
    url: `${SITE_URL}/${event.city}/event/${event.id}`,
    organizer: { "@type": "Organization", name: team || SITE_NAME, url: SITE_URL },
    ...(team ? { performer: { "@type": "SportsTeam", name: team } } : {}),
    ...(event.ticket_url ? { offers: { "@type": "Offer", url: event.ticket_url, availability: "https://schema.org/InStock", category: "primary" } } : {}),
  };

  return (
    <div className="App">
      <Seo title={seoTitle} description={seoDesc} path={`/${event.city}/event/${event.id}`} type="article" image={event.image} jsonLd={jsonLd} />
      <Navbar />
      <div className="relative h-72 sm:h-96 overflow-hidden border-b border-white/10">
        <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ec-bg via-black/40 to-black/30" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 pb-8">
            <span className="overline bg-ec-volt text-black px-2.5 py-1">{sportLabel(event.sport)}</span>
            <h1 data-testid="event-detail-title" className="font-heading text-5xl sm:text-7xl leading-[0.85] tracking-tight mt-4">
              {event.title}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <Link to={`/${city}`} data-testid="event-back-btn" className="inline-flex items-center gap-2 overline text-zinc-400 hover:text-ec-volt transition-colors mb-8">
          <ArrowLeft size={15} /> Back to Calendar
        </Link>

        <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-8">
          <Info icon={CalendarDays} label="Date" value={d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} />
          <Info icon={Clock} label="Kick-off" value={d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} />
          <Info icon={MapPin} label="Venue" value={event.venue} />
          <Info icon={Tag} label="Team" value={event.team ? teamLabel(event.team) : "—"} />
        </div>

        {event.description && (
          <p className="text-zinc-300 leading-relaxed text-lg mb-8">{event.description}</p>
        )}

        <div className="flex flex-wrap gap-3">
          {event.ticket_url && (
            <a
              href={event.ticket_url}
              target="_blank"
              rel="noreferrer"
              data-testid="event-tickets-btn"
              className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-6 py-3.5 hover:-translate-y-0.5 hover:shadow-[0_0_18px_rgba(204,255,0,0.4)] transition-all"
            >
              <Ticket size={16} /> More info / Buy tickets
            </a>
          )}
          <a
            href={single}
            data-testid="event-add-calendar-btn"
            className="inline-flex items-center gap-2 overline border border-white/15 px-6 py-3.5 text-zinc-200 hover:border-ec-volt hover:text-ec-volt transition-colors"
          >
            <CalendarPlus size={16} /> Add this team to my calendar
          </a>
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="bg-ec-surface p-6">
      <div className="flex items-center gap-2 text-zinc-500 mb-2">
        <Icon size={16} />
        <span className="overline">{label}</span>
      </div>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
